import React from 'react';
import {
  Dropdown,
  DropdownItem,
  DropdownList,
  EmptyState,
  EmptyStateBody,
  EmptyStateVariant,
  MenuToggle,
  MenuToggleElement,
  NotificationDrawer,
  NotificationDrawerBody,
  NotificationDrawerHeader,
  NotificationDrawerList,
  NotificationDrawerListItem,
  NotificationDrawerListItemBody,
  NotificationDrawerListItemHeader,
} from '@patternfly/react-core';
import { EllipsisVIcon, SearchIcon } from '@patternfly/react-icons';
import { useNotifications } from '../../contexts/NotificationContext';

export const GlobalNotificationDrawer: React.FC = () => {
  const { notifications, removeNotification, markAsRead, markAllAsRead, clearAll, setDrawerOpen } = useNotifications();
  const [openDropdownKey, setOpenDropdownKey] = React.useState<React.Key | null>(null);

  const unreadCount = notifications.filter((n) => !n.isNotificationRead).length;

  const onDropdownSelect = () => {
    setOpenDropdownKey(null);
  };

  const onDropdownToggle = (key: React.Key) => {
    setOpenDropdownKey(openDropdownKey === key ? null : key);
  };

  return (
    <NotificationDrawer>
      <NotificationDrawerHeader count={unreadCount} onClose={() => setDrawerOpen(false)}>
        <Dropdown
          isOpen={openDropdownKey === 'header-actions'}
          onSelect={onDropdownSelect}
          popperProps={{ position: 'right' }}
          toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
            <MenuToggle
              ref={toggleRef}
              isExpanded={openDropdownKey === 'header-actions'}
              variant="plain"
              onClick={() => onDropdownToggle('header-actions')}
              aria-label="Notification drawer actions"
            >
              <EllipsisVIcon aria-hidden="true" />
            </MenuToggle>
          )}
        >
          <DropdownList>
            <DropdownItem key="markAllRead" onClick={markAllAsRead}>
              Mark all read
            </DropdownItem>
            <DropdownItem key="clearAll" onClick={clearAll}>
              Clear all
            </DropdownItem>
          </DropdownList>
        </Dropdown>
      </NotificationDrawerHeader>
      <NotificationDrawerBody>
        {notifications.length === 0 ? (
          <EmptyState variant={EmptyStateVariant.full} icon={SearchIcon} titleText="No notifications found">
            <EmptyStateBody>There are currently no notifications.</EmptyStateBody>
          </EmptyState>
        ) : (
          <NotificationDrawerList>
            {notifications.map((notification, index) => (
              <NotificationDrawerListItem
                key={notification.key}
                variant={notification.variant}
                isRead={notification.isNotificationRead}
                onClick={() => markAsRead(notification.key)}
              >
                <NotificationDrawerListItemHeader
                  variant={notification.variant}
                  title={notification.title}
                  srTitle={notification.srTitle}
                >
                  <Dropdown
                    isOpen={openDropdownKey === notification.key}
                    onSelect={onDropdownSelect}
                    popperProps={{ position: 'right' }}
                    toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                      <MenuToggle
                        ref={toggleRef}
                        isExpanded={openDropdownKey === notification.key}
                        variant="plain"
                        onClick={() => onDropdownToggle(notification.key)}
                        aria-label={`Notification ${index + 1} actions`}
                      >
                        <EllipsisVIcon aria-hidden="true" />
                      </MenuToggle>
                    )}
                  >
                    <DropdownList>
                      <DropdownItem onClick={() => markAsRead(notification.key)}>Mark as read</DropdownItem>
                      <DropdownItem onClick={() => removeNotification(notification.key)}>Clear</DropdownItem>
                    </DropdownList>
                  </Dropdown>
                </NotificationDrawerListItemHeader>
                <NotificationDrawerListItemBody timestamp={notification.timestamp}>
                  {notification.description}
                </NotificationDrawerListItemBody>
              </NotificationDrawerListItem>
            ))}
          </NotificationDrawerList>
        )}
      </NotificationDrawerBody>
    </NotificationDrawer>
  );
};
